import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/cn';

export const Tabs = TabsPrimitive.Root;

export function TabsList({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn(
        // Horizontal scroll on overflow keeps the tab bar one-line at every
        // viewport — the wrap-into-3-lines bug is gone. `max-w-full` lets the
        // bar shrink to its parent; `scrollbar-thin` keeps the scrollbar tidy.
        // `overflow-y-hidden` is mandatory next to `overflow-x-auto`: the CSS
        // spec promotes `overflow-y: visible` to `auto` when overflow-x isn't
        // visible, which produced a stray vertical scrollbar on focus rings.
        'flex h-11 max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-1 scrollbar-thin',
        className,
      )}
      {...rest}
    />
  );
}

export function TabsTrigger({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap rounded-lg px-4 text-sm font-semibold text-[var(--color-fg-muted)] transition-colors',
        'hover:text-[var(--color-fg-primary)]',
        'data-[state=active]:bg-[var(--color-accent)] data-[state=active]:text-[var(--color-fg-on-accent)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]',
        className,
      )}
      {...rest}
    />
  );
}

export function TabsContent({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn('mt-6', className)} {...rest} />;
}
